import type { KeyboardEvent, MouseEvent } from "react";
import type { Product } from "../types/marketplace";
import { formatCurrency, formatNumber, formatNumericText } from "../utils/formatters";

const truckIcon = "\u{1F69A}";
const lightningIcon = "\u26A1";
const activeStars = "\u2605\u2605\u2605\u2605";
const emptyStar = "\u2605";

interface CardItemProps {
  item: Product;
  compact?: boolean;
  isCompared?: boolean;
  onCompareToggle?: (item: Product) => void;
  onSelect?: (item: Product) => void;
}

function formatSales(value: string): string {
  const match = value.match(/^([+]?)(\d[\d.]*)((?:\s|$).*)$/);

  if (!match) {
    return value;
  }

  return `${match[1]}${formatNumber(Number(match[2].replace(/\./g, "")))}${match[3]}`;
}

function CardItem({
  item,
  compact = false,
  isCompared = false,
  onCompareToggle,
  onSelect
}: CardItemProps) {
  const isOutOfStock = (item.stock ?? 0) <= 0;

  const handleSelect = () => {
    onSelect?.(item);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect();
    }
  };

  const handleCompareClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCompareToggle?.(item);
  };

  const handleBuyClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isOutOfStock) {
      return;
    }

    handleSelect();
  };

  return (
    <article
      className={`product-card ${compact ? "compact" : ""} ${onSelect ? "clickable" : ""}`}
      onClick={handleSelect}
      onKeyDown={onSelect ? handleKeyDown : undefined}
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
    >
      <div className="image-wrap">
        {isOutOfStock ? (
          <span className="discount out-of-stock">Sin stock</span>
        ) : (
          item.discount && <span className="discount">{item.discount}</span>
        )}
        <img src={item.image} alt={item.name} decoding="async" loading="lazy" />
      </div>

      <div className="product-info">
        <div className="price-row">
          <strong>{formatCurrency(item.price)}</strong>
          {item.oldPrice && <del>{formatCurrency(item.oldPrice)}</del>}
        </div>

        {!compact && <p className="installments">{"Hasta 6 cuotas sin inter\u00e9s"}</p>}

        {!compact && (
          <p className="shipping">
            {truckIcon} {"Env\u00edo gratis"}{" "}
            {item.full && (
              <span>
                {lightningIcon} FULL
              </span>
            )}
          </p>
        )}

        <h3>{item.name}</h3>

        {item.clientName && <p className="store-name">{item.clientName}</p>}
        {isOutOfStock && <p className="stock-status">Sin stock</p>}

        {!compact && (
          <p className="rating">
            {activeStars}
            <span>{emptyStar}</span> <small>({formatNumericText(item.rating)})</small>
          </p>
        )}

        <p className="sales">{formatSales(item.sales)}</p>

        {(onSelect || onCompareToggle) && (
          <div className="product-actions">
            {onSelect && (
              <button
                className="buy-card-button"
                disabled={isOutOfStock}
                onClick={handleBuyClick}
                type="button"
              >
                {isOutOfStock ? "Sin stock" : "Comprar"}
              </button>
            )}

            {onCompareToggle && (
              <button
                className={`compare-card-button ${isCompared ? "selected" : ""}`}
                onClick={handleCompareClick}
                type="button"
              >
                {isCompared ? "Comparando" : "Comparar"}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default CardItem;
